<script lang="ts">
	import { formatCreditFactor, getModelCreditFactor } from '#src/constants/model-credit-factor.ts';
	import { AVAILABLE_MODELS } from '@astlai/common/util/models';
	import { globalState } from '#lib/util/global-state.svelte.ts';
	import ModelIcon from '#lib/lib/icons/ModelIcon.svelte';
	import Select from '#lib/Select.svelte';
	import Option from '#lib/select/Option.svelte';
	import OptionGroup from '#lib/select/OptionGroup.svelte';
	import { tooltip } from '@astlai/common/tooltip/tooltip.svelte.ts';
	import { t } from '#lib/i18n/tranlsator.svelte.ts';
	import AdminOnlyIcon from 'virtual:icons/material-symbols/lock-outline';

	interface Props {
		model: string;
		label?: string;
		onchange?: (v: string) => void;
	}

	let { model = $bindable(), onchange = () => {}, label }: Props = $props();

	const isAdmin = $derived(globalState.user?.role === 'admin');

	const grouped = $derived(
		Object.entries(
			AVAILABLE_MODELS.filter(
				(m) => (!m.eol || m.key === model) && (!m.adminOnly || isAdmin)
			).reduce(
				(acc, val) => ({ ...acc, [val.vendor]: [...(acc[val.vendor] || []), val] }),
				{} as Record<string, { key: string; name: string; vendor: string; adminOnly?: boolean }[]>
			)
		)
	);
</script>

<label>
	{#if label}
		<span>{label}</span>
	{/if}
	<Select compact={!label} {onchange} bind:value={model} minWidth={280}>
		{#snippet display(selected)}
			{#if selected[0]}
				<div class="model-row model-display">
					<div class="model-icon"><ModelIcon model={String(selected[0].value)} /></div>
					<span class="model-label">{selected[0].label}</span>
				</div>
			{/if}
		{/snippet}
		{#each grouped as [vendor, group] (vendor)}
			<OptionGroup label={vendor}>
				{#each group as groupModel (groupModel.key)}
					<Option value={groupModel.key} label={groupModel.name}>
						<div class="model-row model-option">
							<div class="model-icon"><ModelIcon model={groupModel.key} /></div>
							<span class="model-label">{groupModel.name}</span>
							{#if groupModel.adminOnly}
								<div class="admin-only-icon" use:tooltip={t('common.adminOnly')}>
									<AdminOnlyIcon />
								</div>
							{/if}
							<span class="model-credits">
								{formatCreditFactor(getModelCreditFactor(groupModel.key))}
							</span>
						</div>
					</Option>
				{/each}
			</OptionGroup>
		{/each}
	</Select>
</label>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.model-row {
		@include lib.flex-row();
		@include lib.flex-align(flex-start, center);
		min-width: 0;

		.model-icon {
			@include lib.flex-row();
			@include lib.flex-align();
			flex-shrink: 0;
			font-size: 0.75rem;
		}

		.model-label {
			min-width: 0;
			@include lib.ellipsis();
		}
	}

	.model-display {
		gap: 0.25rem;
	}

	.model-option {
		gap: 0.5rem;
		width: 100%;

		.model-icon {
			color: var(--text-tertiary);
		}

		.model-label {
			flex: 1 1 auto;
		}

		.model-credits {
			flex-shrink: 0;
			font-size: 0.75rem;
			color: var(--text-tertiary);
		}

		.admin-only-icon {
			@include lib.flex-row();
			@include lib.flex-align();
			flex-shrink: 0;
			font-size: 0.875rem;
			color: var(--text-tertiary);
		}
	}
</style>
